<template>
  <tr class="flex w-full py-2">
    <!-- Scope -->
    <td class="w-3/12 flex items-center h-10 pl-2"
      :class="{'pr-4': isAddMode}"
    >
      <!-- ADD MODE -->
      <template v-if="isAddMode">
        <variable-scope-picker v-model="editScopeSelection"/>
      </template>

      <!-- DISPLAY MODE -->
      <template v-else>
        <variable-scope-badge
          :scope="scopeLevel"
          :bot-name="scopeBotName"
          :market-name="scopeMarketName"
          :bot-link="scopeBotLink"
          :market-link="scopeMarketLink"
          @navigate="onMarketBadgeNavigate"
        />
      </template>
    </td>

    <!-- Name -->
    <td class="w-3/12 flex items-center"
      @mouseenter="isNameHovered = true"
      @mouseleave="isNameHovered = false"
    >
      <div class="relative flex items-center w-full pl-2"
        :class="{'pr-4': isAddMode}"
      >
        <template v-if="isAddMode">
          <!-- bot+market: name can only shadow an existing group variable name (§1 invariant),
               so it suggests existing global/group names to override, while still allowing a new name. -->
          <queryable-select v-if="isBotMarketScope"
            v-model="editName"
            id="variable-name"
            :options="groupVariableNameOptions"
            :allow-custom-option="true"
            value-attribute="value"
            text-attribute="text"
            class="w-full h-10"
          />
          <base-input v-else
            v-model="editName"
            ref="nameInput"
            :variant="nameError ? 'table-cell-error' : 'table-cell'"
            class="w-full h-10"
            @keydown.esc.native="onCancel"
            @keydown.enter.native="focusValueInput"
          />
        </template>
        <template v-else-if="editingField === 'name'">
          <input-with-error-message
            :key="`name-${variable.id}-${inlineEditKey}`"
            :id="`variable-name-${variable.id}`"
            :value.sync="inlineEditName"
            placeholder="Variable name"
            :error-message="nameError || 'Name cannot be empty'"
            class="w-full h-10"
            :show-cancel-button="true"
            @escape="onInlineNameEscape"
            @submit="onInlineNameSubmit"
          />
        </template>
        <template v-else>
          <vx-tooltip
            v-if="isBotMarketOverride"
            text="Rename the group or global variable instead."
            position="top"
            :enabled="isNameHovered"
            class="w-full"
          >
            <div class="w-full h-10 flex items-center border border-transparent rounded border-solid cursor-not-allowed opacity-75">
              <div class="relative flex min-w-0 items-center">
                <span class="text-lg truncate pl-5">{{ variable.name }}</span>
              </div>
            </div>
          </vx-tooltip>
          <div
            v-else
            class="w-full h-10 flex items-center border border-transparent rounded border-solid cursor-text"
            :class="{'hover:border-gray-border': !isCopyNameHovered}"
            @click="openNameEdit"
          >
            <div class="relative flex min-w-0 items-center">
              <span
                class="absolute left-0 text-lg text-gray mr-4"
                :class="isCopyNameHovered ? 'opacity-100' : 'opacity-0'"
              >${</span>
              <span class="text-lg truncate pl-5">{{ variable.name }}</span>
              <span
                class="ml-0.5 flex-shrink-0 text-lg text-gray"
                :class="isCopyNameHovered ? 'opacity-100' : 'opacity-0'"
              >}</span>
            </div>
            <CopyButton
              class="ml-3"
              compact
              :text="copyNameText"
              copied-label="Path copied!"
              :visible="isNameHovered"
              @hover="isCopyNameHovered = $event"
            />
          </div>
        </template>
      </div>
    </td>

    <!-- Value -->
    <td class="w-5/12 flex items-center"
      @mouseenter="isValueHovered = true"
      @mouseleave="isValueHovered = false"
    >
      <div class="relative flex items-center w-full"
        :class="{'pr-4': isAddMode}"
      >
        <template v-if="isAddMode">
          <base-input v-model="editValue"
            ref="valueInput"
            :variant="valueError ? 'table-cell-error' : 'table-cell'"
            class="w-full h-10"
            @keydown.esc="onCancel"
          />
        </template>
        <template v-else-if="editingField === 'value'">
          <input-with-error-message
            :key="`value-${variable.id}-${inlineEditKey}`"
            :id="`variable-value-${variable.id}`"
            :value.sync="inlineEditValue"
            placeholder="Variable value"
            :error-message="valueError || 'Value cannot be empty'"
            class="w-full h-10"
            :show-cancel-button="true"
            @escape="onInlineValueEscape"
            @submit="onInlineValueSubmit"
          />
        </template>
        <template v-else>
          <div
            class="w-full h-10 flex items-center border border-transparent rounded border-solid cursor-text"
            :class="{'hover:border-gray-border': !isCopyValueHovered}"
            @click="openValueEdit"
          >
            <span class="text-lg truncate pl-3">{{ variable.value }}</span>
            <copy-button
              class="ml-3"
              compact
              :text="copyValueText"
              copied-label="Value copied!"
              :visible="isValueHovered"
              @hover="isCopyValueHovered = $event"
            />
          </div>
        </template>
      </div>
    </td>

    <!-- Actions -->
    <td class="w-1/12 flex items-center gap-1 pr-3">
      <template v-if="isAddMode">
        <vx-tooltip
          text="Save"
          position="bottom"
          class="flex items-center justify-center h-10 w-10 rounded-full cursor-pointer bg-success hover:opacity-80"
          @click.native="onConfirm"
        >
          <checkmark-icon class="h-5 w-5 text-white"/>
        </vx-tooltip>
        <vx-tooltip
          text="Cancel"
          position="bottom"
          class="flex items-center justify-center h-10 w-10 rounded-full cursor-pointer bg-focus-error hover:opacity-80"
          @click.native="onCancel"
        >
          <close-icon class="h-5 w-5 text-white"/>
        </vx-tooltip>
      </template>

      <template v-else>
        <vx-tooltip v-if="variable.id !== ITEM_IDS.NEW"
          text="View Variable Usages"
          position="bottom"
          class="flex items-center justify-center h-10 w-10 rounded-full cursor-pointer text-gray-dark bg-soft hover:opacity-80"
          @click.native="$emit('view-variable-usages', variable.id)"
        >
          <eye-icon class="h-5 w-5"/>
        </vx-tooltip>
        <vx-tooltip text="Delete variable"
          position="bottom"
          class="flex items-center justify-center h-10 w-10 rounded-full cursor-pointer text-focus-error bg-focus-error-light hover:opacity-80"
          @click.native="onDelete"
        >
          <delete-icon class="h-5 w-5"/>
        </vx-tooltip>
      </template>
    </td>
  </tr>
</template>

<script>
import variablesModule from '@/store/modules/variables.module'
import variablesService from '@/services/amio/variables.service'
import botModule from '@/store/modules/bot.module'
import marketModule from '@/store/modules/market.module'
import VariableScopeBadge from '@/components/settings/variables/VariableScopeBadge.vue'
import VariableScopePicker from '@/components/settings/variables/VariableScopePicker.vue'
import { getLogicalBotOptions, resolveBotRouteId, variableScopePayload, variableScopeSelection, variableScopeLevel } from '@/utils/market.utils'
import BaseInput from '@/components/base-components/BaseInput.vue'
import QueryableSelect from '@/components/common/QueryableSelect.vue'
import CopyButton from '@/components/common/CopyButton.vue'
import EyeIcon from '@/assets/images/icons/EyeIcon.vue'
import DeleteIcon from '@/assets/images/icons/DeleteIcon.vue'
import CheckmarkIcon from '@/assets/images/icons/CheckmarkIcon.vue'
import CloseIcon from '@/assets/images/icons/CloseIcon.vue'
import InputWithErrorMessage from '@/components/common/InputWithErrorMessage.vue'
import { ITEM_IDS } from '@/utils/editor.constants'
import { VARIABLE_COLS, VARIABLE_SCOPE } from '@/store/constants'
import errorUtils from '@/utils/error.utils'
import toaster from '@/utils/toaster'

export default {
  name: 'VariablesTableRow',

  components: {
    BaseInput,
    QueryableSelect,
    CopyButton,
    EyeIcon,
    DeleteIcon,
    CheckmarkIcon,
    CloseIcon,
    InputWithErrorMessage,
    VariableScopeBadge,
    VariableScopePicker
  },

  props: {
    variable: {
      type: Object,
      required: true
    },
    storeIndex: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      isNameHovered: false,
      isValueHovered: false,
      isCopyNameHovered: false,
      isCopyValueHovered: false,
      inlineEditKey: 0,
      editingField: null,
      inlineEditName: '',
      inlineEditValue: '',
      editName: '',
      editValue: '',
      editScopeSelection: { scope: VARIABLE_SCOPE.GLOBAL },
      ITEM_IDS
    }
  },

  computed: {
    bots: botModule.getBotList,

    editedId() {
      return variablesModule.editedId.get()
    },

    variablesList() {
      return variablesModule.variables.get()
    },

    isAddMode() {
      return this.variable.id === ITEM_IDS.NEW
    },

    isEditMode() {
      return !this.isAddMode && this.editedId === this.variable.id
    },

    scopeLevel() {
      return variableScopeLevel(this.variable)
    },

    // The logical bot a bot/bot+market variable belongs to (group id, or standalone bot id).
    scopeLogicalBotId() {
      if (this.variable.multi_bot_id) return this.variable.multi_bot_id
      if (this.variable.bot_id) {
        const bot = this.bots.find(b => b.id === this.variable.bot_id)
        return (bot && bot.multi_bot_id) ? bot.multi_bot_id : this.variable.bot_id
      }
      return null
    },

    // The scope currently chosen in the add-mode picker is bot+market.
    isBotMarketScope() {
      return this.editScopeSelection && this.editScopeSelection.scope === VARIABLE_SCOPE.BOT_MARKET
    },

    // An existing bot+market variable that OVERRIDES a higher-level same-name variable (its group's
    // multibot, or global). Its name is owned by the parent, so it can't be renamed here — rename
    // the parent (which cascades down). Independent market-only bot+market variables stay editable.
    isBotMarketOverride() {
      if (!this.variable.bot_id) return false
      const bot = this.bots.find(b => b.id === this.variable.bot_id)
      if (!bot || !bot.multi_bot_id) return false
      return variablesModule.variables.get().some(v =>
        v.id !== this.variable.id && v.name === this.variable.name &&
        ((!v.bot_id && !v.multi_bot_id) || (!v.bot_id && v.multi_bot_id === bot.multi_bot_id))
      )
    },

    // The group's multibot variable names available to shadow for the selected variant
    // (excludes names already overridden on that variant). The variant bot id is resolved from
    // the picker selection (group multi_bot_id + market_id) via the same util the payload uses.
    groupVariableNameOptions() {
      if (!this.isBotMarketScope) return []
      const multiBotId = this.editScopeSelection.scope_value || null
      const variantBotId = variableScopePayload(this.editScopeSelection).bot_id
      return variablesModule
        .groupVariableNames(multiBotId, variantBotId)
        .map(name => ({ value: name, text: name }))
    },

    logicalBotOptions() {
      return getLogicalBotOptions()
    },

    scopeBotName() {
      const logicalBotId = this.scopeLogicalBotId
      if (!logicalBotId) return ''
      const option = this.logicalBotOptions.find(o => o.value === logicalBotId)
      return option ? option.text : logicalBotId
    },

    scopeBotLink() {
      if (this.scopeLevel !== VARIABLE_SCOPE.BOT) return null
      const botId = resolveBotRouteId(this.scopeLogicalBotId)
      return botId ? `/settings/bots/${botId}` : null
    },

    // The market name for a "bot+market" variant.
    scopeMarketName() {
      if (this.scopeLevel === VARIABLE_SCOPE.BOT_MARKET) {
        const variant = this.bots.find(b => b.id === this.variable.bot_id)
        const marketId = variant ? variant.market_id : null
        const market = marketModule.getMarketList().find(m => m.id === marketId)
        return market ? market.name : (marketId || '')
      }
      return ''
    },

    scopeMarketLink() {
      if (this.scopeLevel !== VARIABLE_SCOPE.BOT_MARKET) return null
      // bot_id already points at the variant bot row.
      return this.variable.bot_id ? `/settings/bots/${this.variable.bot_id}?panel=market` : null
    },

    copyNameText() {
      return `\${variable.${this.variable.name}}`
    },

    copyValueText() {
      return String(this.variable.value || '')
    },

    nameError: {
      get() {
        return variablesModule.variableError.get(this.variable.id, VARIABLE_COLS.NAME)
      },
      set(val) {
        variablesModule.variableError.set(this.variable.id, VARIABLE_COLS.NAME, val)
      }
    },

    valueError: {
      get() {
        return variablesModule.variableError.get(this.variable.id, VARIABLE_COLS.VALUE)
      },
      set(val) {
        variablesModule.variableError.set(this.variable.id, VARIABLE_COLS.VALUE, val)
      }
    }
  },

  watch: {
    'variable.id': {
      handler() {
        this.syncEditState()
      }
    },
    isEditMode: {
      immediate: true,
      handler(active) {
        if (active) this.syncEditState()
      }
    },
    isAddMode: {
      immediate: true,
      handler(active) {
        if (active) {
          this.syncEditState()
          this.$nextTick(() => this.focusNameInput())
        }
      }
    }
  },

  methods: {
    syncEditState() {
      this.editName = this.variable.name || ''
      this.editValue = this.variable.value || ''
      this.editScopeSelection = this.buildSelectionFromVariable()
    },

    buildSelectionFromVariable() {
      return variableScopeSelection(this.variable)
    },

    scopePayloadForCreate() {
      return variableScopePayload(this.editScopeSelection)
    },

    onMarketBadgeNavigate(link) {
      this.$router.push(link).catch(() => {})
    },

    openNameEdit() {
      if (this.isBotMarketOverride) return // rename the parent (group/global) variable instead
      this.inlineEditName = this.variable.name || ''
      this.inlineEditKey += 1
      this.editingField = 'name'
    },

    openValueEdit() {
      this.inlineEditValue = this.variable.value != null ? String(this.variable.value) : ''
      this.inlineEditKey += 1
      this.editingField = 'value'
    },

    onInlineNameEscape() {
      this.inlineEditName = this.variable.name || ''
      this.editingField = null
      this.nameError = null
    },

    onInlineValueEscape() {
      this.inlineEditValue = this.variable.value != null ? String(this.variable.value) : ''
      this.editingField = null
      this.valueError = null
    },

    async onInlineNameSubmit(newName) {
      if (newName === this.variable.name) {
        this.editingField = null
        return
      }

      await this.updateVariableWithPayload({ name: newName })
      if (!variablesModule.variableError.get(this.variable.id, VARIABLE_COLS.NAME)) {
        this.editingField = null
      }
    },

    async onInlineValueSubmit(newValue) {
      if (newValue === this.variable.value) {
        this.editingField = null
        return
      }

      await this.updateVariableWithPayload({ value: newValue })
      if (!variablesModule.variableError.get(this.variable.id, VARIABLE_COLS.VALUE)) {
        this.editingField = null
      }
    },

    focusNameInput() {
      const ref = this.$refs.nameInput
      if (ref && ref.$el) ref.$el.focus()
    },

    focusValueInput() {
      const ref = this.$refs.valueInput
      if (ref && ref.$el) ref.$el.focus()
    },

    onConfirm() {
      if (this.isAddMode) {
        this.createVariable()
      }
    },

    async createVariable() {
      this.nameError = null
      this.valueError = null
      try {
        variablesModule.variablesLoading.set(true)
        const payload = {
          name: this.editName.trim(),
          value: this.editValue.trim(),
          ...this.scopePayloadForCreate()
        }
        const newVariable = await variablesService.create(payload)
        variablesModule.setVariable(0, newVariable)
        variablesModule.editedId.set(null)
      } catch (err) {
        const msg = this.resolveCreateErrorMessage(err)
        this.nameError = msg
        toaster.error(msg)
      } finally {
        variablesModule.variablesLoading.set(false)
      }
    },

    // For a rejected create, prefer the server's description on the invariant/conflict responses
    // (422 e.g. BOT_MARKET_REQUIRES_GROUP, 409 duplicity/usage) so the precise reason is shown;
    // fall back to the generic resolved message otherwise.
    resolveCreateErrorMessage(err) {
      const response = err.response || {}
      const status = response.status
      const data = response.data
      if (status === 422 || status === 409) {
        const description = data && ((data.details && data.details.description) || data.description)
        if (description) return description
      }
      return errorUtils.resolveMessage(err)
    },

    async updateVariableWithPayload(payload) {
      const hasName = 'name' in payload
      const hasValue = 'value' in payload
      if (hasName) this.nameError = null
      if (hasValue) this.valueError = null
      const oldName = this.variable.name
      const multiBotId = this.variable.multi_bot_id
      try {
        variablesModule.variablesLoading.set(true)
        const body = { ...payload }
        if (typeof body.name === 'string') body.name = body.name.trim()
        if (typeof body.value === 'string') body.value = body.value.trim()
        const updated = await variablesService.update(this.variable.id, body)
        variablesModule.setVariable(this.storeIndex, updated)
        // A rename cascades server-side to same-name variables that follow it. Mirror that in the
        // store so the cascaded rows show the new name without a refresh:
        //   - multibot (group) rename → the group's bot+market overrides;
        //   - global rename → bot+market overrides anchored to global (their group has no same-name
        //     multibot; a group with one is insulated and follows a multibot rename instead);
        //   - bot+market rename → its same-name siblings in the same group (an orphan market-only
        //     variable shared across markets; the siblings are the same logical variable).
        if (updated.name !== oldName) {
          if (multiBotId) {
            this.propagateGroupRenameToOverrides(multiBotId, oldName, updated.name)
          } else if (!this.variable.bot_id) {
            this.propagateGlobalRenameToOverrides(oldName, updated.name)
          } else {
            const ownerBot = this.bots.find(candidate => candidate.id === this.variable.bot_id)
            if (ownerBot && ownerBot.multi_bot_id) {
              this.propagateGroupRenameToOverrides(ownerBot.multi_bot_id, oldName, updated.name)
            }
          }
        }
        variablesModule.variableError.set(this.variable.id, VARIABLE_COLS.NAME, null)
        variablesModule.variableError.set(this.variable.id, VARIABLE_COLS.VALUE, null)
      } catch (err) {
        const data = err.response && err.response.data
        const code = data && data.code
        const isValidation = code && typeof code.includes === 'function' && code.includes('validation')
        const msg = errorUtils.resolveMessage(err)
        const desc = (data && data.details && data.details.description) || msg
        const errorMsg = isValidation ? desc : msg
        if (hasName) this.nameError = errorMsg
        if (hasValue) this.valueError = errorMsg
        toaster.error(errorMsg)
      } finally {
        variablesModule.variablesLoading.set(false)
      }
    },

    // Rename the group's bot+market override rows (variant bot_id within this group, same old
    // name) in the store, matching the server-side cascade of a multibot rename.
    propagateGroupRenameToOverrides(multiBotId, oldName, newName) {
      const variantBotIds = new Set(
        this.bots.filter(b => b.multi_bot_id === multiBotId).map(b => b.id)
      )
      const list = variablesModule.variables.get().map(v =>
        (v.bot_id && variantBotIds.has(v.bot_id) && v.name === oldName) ?
          { ...v, name: newName } :
          v
      )
      variablesModule.variables.set(list)
    },

    // Rename the bot+market override rows anchored to a renamed GLOBAL variable, matching the
    // server-side cascade: a bot+market override (variant bot_id) of the same name follows the
    // global rename UNLESS its group has its own same-name multibot variable (then that group is
    // insulated and its overrides follow the multibot rename instead).
    propagateGlobalRenameToOverrides(oldName, newName) {
      const list = variablesModule.variables.get()
      const insulatedGroupIds = new Set(
        list.filter(v => v.multi_bot_id && !v.bot_id && v.name === oldName).map(v => v.multi_bot_id)
      )
      const groupIdOfBot = botId => {
        const bot = this.bots.find(candidate => candidate.id === botId)
        return bot && bot.multi_bot_id
      }
      const updatedList = list.map(v => {
        if (!v.bot_id || v.name !== oldName) return v
        const groupId = groupIdOfBot(v.bot_id)
        return groupId && !insulatedGroupIds.has(groupId) ? { ...v, name: newName } : v
      })
      variablesModule.variables.set(updatedList)
    },

    onCancel() {
      if (this.isAddMode) {
        const list = this.variablesList.filter(v => v.id !== ITEM_IDS.NEW)
        variablesModule.variables.set(list)
      }
      variablesModule.editedId.set(null)
      this.nameError = null
      this.valueError = null
      this.syncEditState()
    },

    async onDelete() {
      if (this.variable.id === ITEM_IDS.NEW) {
        const list = this.variablesList.filter(v => v.id !== ITEM_IDS.NEW)
        variablesModule.variables.set(list)
        variablesModule.editedId.set(null)
        return
      }
      try {
        variablesModule.variablesLoading.set(true)
        await variablesService.delete(this.variable.id)
        const list = this.variablesList.filter(v => v.id !== this.variable.id)
        variablesModule.variables.set(list)
        variablesModule.variableError.set(this.variable.id, VARIABLE_COLS.NAME, null)
        variablesModule.variableError.set(this.variable.id, VARIABLE_COLS.VALUE, null)
      } catch (err) {
        const msg = errorUtils.resolveMessage(err)
        toaster.error(msg)
        if (msg.includes('used')) {
          this.$emit('view-variable-usages', this.variable.id)
        }
      } finally {
        variablesModule.variablesLoading.set(false)
      }
    }
  }
}
</script>
